import { loadDetailFromUrl, renderDetail } from './detail';
import { pickSessionGag } from './gags';
import { detectInitialLocale, t, type Locale } from './i18n';
import { render, type Handlers, type ViewState } from './state';
import type { StoredDetail } from './detail-store';
import {
  disablePush,
  enablePush,
  getStoredLocale,
  loadCurrentSubscription,
  needsIosInstall,
  setStoredLocale,
  supportsPush,
  updatePreferences,
  type Frequency,
} from './push';

const heroEl = document.getElementById('hero') as HTMLElement;
const langButtons = document.querySelectorAll<HTMLButtonElement>('.lang-btn');

let locale: Locale = detectInitialLocale(getStoredLocale());
let state: ViewState = { kind: 'checking' };
let activeFrequency: Frequency = 'occasionally';

// A notification opened from a push takes precedence over the normal
// landing state for this page load: it renders regardless of subscription
// status, and doesn't wait on subscription reconciliation to appear. It's a
// layer on top of the state machine, not a state in it, so `state` above is
// unaffected and reconciliation keeps running underneath it.
let detail: { entry: StoredDetail; gag: number } | null = null;

function rerender(): void {
  if (detail) {
    renderDetail(heroEl, detail.entry, detail.gag, locale, closeDetail);
    return;
  }
  render(heroEl, state, locale, handlers);
}

function closeDetail(): void {
  detail = null;
  rerender();
}

function setState(next: ViewState): void {
  state = next;
  // While the detail is showing, subscription-state updates still land in
  // `state` (so whatever comes after matches reality) but are held back
  // from the DOM until the detail is closed, so it doesn't flicker away.
  if (!detail) rerender();
}

function applyStaticI18n(): void {
  document.documentElement.lang = locale;
  document.querySelectorAll<HTMLElement>('[data-i18n]').forEach((el) => {
    const key = el.dataset.i18n as Parameters<typeof t>[0];
    el.textContent = t(key, locale);
  });
  langButtons.forEach((btn) => {
    btn.setAttribute('aria-pressed', String(btn.dataset.lang === locale));
  });
}

async function setLocale(next: Locale): Promise<void> {
  if (next === locale) return;
  locale = next;
  setStoredLocale(locale);
  applyStaticI18n();
  rerender();
  if (state.kind === 'active') {
    try {
      await updatePreferences({ locale });
    } catch {
      // Non-fatal: the UI already reflects the new language; the backend
      // sync will effectively retry next time preferences are saved.
    }
  }
}

const handlers: Handlers = {
  onSelectFrequency(freq) {
    if (state.kind === 'idle') {
      activeFrequency = freq;
      setState({ kind: 'idle', frequency: freq });
    } else if (state.kind === 'active') {
      setState({ ...state, pending: freq });
    }
  },

  async onEnable() {
    setState({ kind: 'enabling' });
    try {
      const result = await enablePush(activeFrequency, locale);
      setState({ kind: 'active', frequency: result.frequency, pending: result.frequency, saving: false });
    } catch (err) {
      if (err instanceof Error && err.name === 'PermissionDeniedError') {
        setState({ kind: 'denied' });
      } else {
        console.error('enablePush failed:', err);
        setState({ kind: 'error' });
      }
    }
  },

  async onSaveFrequency() {
    if (state.kind !== 'active') return;
    const pending = state.pending;
    setState({ ...state, saving: true });
    try {
      const result = await updatePreferences({ frequency: pending });
      activeFrequency = result.frequency;
      setState({ kind: 'active', frequency: result.frequency, pending: result.frequency, saving: false });
    } catch {
      setState({ kind: 'active', frequency: state.frequency, pending, saving: false });
    }
  },

  async onDisable() {
    setState({ kind: 'disabling' });
    try {
      await disablePush();
    } finally {
      setState({ kind: 'disabled' });
    }
  },

  onEnableAgain() {
    setState({ kind: 'idle', frequency: activeFrequency });
  },

  onRetry() {
    setState({ kind: 'idle', frequency: activeFrequency });
  },
};

langButtons.forEach((btn) => {
  btn.addEventListener('click', () => {
    const next = btn.dataset.lang;
    if (next === 'en' || next === 'es') void setLocale(next);
  });
});

async function init(): Promise<void> {
  applyStaticI18n();

  const hasNotifId = new URL(location.href).searchParams.has('notifid');
  if (hasNotifId) {
    // Blank the statically-rendered headline immediately so it doesn't
    // flash before we know whether there's a notification to show instead.
    heroEl.innerHTML = '';
  } else {
    rerender();
  }

  try {
    const entry = await loadDetailFromUrl({
      href: location.href,
      replaceState: (url) => history.replaceState(null, '', url),
      caches,
      now: Date.now(),
    });
    if (entry) detail = { entry, gag: pickSessionGag() };
  } catch {
    /* No stored payload, or the lookup failed — fall through to the normal landing state. */
  }
  rerender();

  if (needsIosInstall()) {
    setState({ kind: 'ios-install-needed' });
    return;
  }
  if (!supportsPush()) {
    setState({ kind: 'unsupported' });
    return;
  }
  if (Notification.permission === 'denied') {
    setState({ kind: 'denied' });
    return;
  }

  try {
    const current = await loadCurrentSubscription();
    if (current) {
      activeFrequency = current.frequency;
      if (current.locale !== locale) {
        locale = current.locale;
        setStoredLocale(locale);
        applyStaticI18n();
      }
      setState({ kind: 'active', frequency: current.frequency, pending: current.frequency, saving: false });
    } else {
      setState({ kind: 'idle', frequency: activeFrequency });
    }
  } catch {
    setState({ kind: 'idle', frequency: activeFrequency });
  }
}

void init();
